// src/components/Header/index.js

import React, { useState, useContext, useRef, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';

import UserContext from '../../contexts/UserContext';
import DealershipContext from '../../contexts/DealershipContext';
import './Header.css';
import LoginModal from '../LoginModal';
import LogoutConfirmationModal from '../LogoutConfirmationModal';
import { useCart } from '../../contexts/CartContext';

const Header = () => {
  const { user, logout } = useContext(UserContext);
  const { dealership } = useContext(DealershipContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const dropdownRef = useRef(null);
  const hamburgerRef = useRef(null);
  const closeButtonRef = useRef(null);
  const wasOpenRef = useRef(false);
  const history = useHistory();

  // While the drawer is open: lock page scroll, close on Escape, move focus in.
  // On close, return focus to the hamburger button.
  useEffect(() => {
    if (menuOpen) {
      wasOpenRef.current = true;
      document.body.style.overflow = 'hidden';
      const onKeyDown = (e) => {
        if (e.key === 'Escape') setMenuOpen(false);
      };
      document.addEventListener('keydown', onKeyDown);
      closeButtonRef.current?.focus();
      return () => {
        document.body.style.overflow = '';
        document.removeEventListener('keydown', onKeyDown);
      };
    }
    if (wasOpenRef.current) {
      wasOpenRef.current = false;
      hamburgerRef.current?.focus();
    }
  }, [menuOpen]);

  const { lineItems } = useCart();
  const cartItemCount = lineItems ? lineItems.length : 0;


  const handleLogout = async () => {
    try {
      await logout();
      setIsLogoutModalOpen(false);
      history.push('/');
    } catch (error) {
    }
  };

  return (
    <header className="header">
      <div className="header-top">
        <div className="header-left">
          <Link to="/" className="logo-container">
            <img src="/images/Cadillac_Logo_Black.png" alt="Logo" className="logo" />
            <span className="app-title">AD BUILDER</span>
          </Link>
        </div>

        <div className="header-right">
          <div className="dealer-user-stack">
            {dealership?.name && (
              <div className="header-dealer-name">
                {dealership.logoUrl && (
                  <img src={dealership.logoUrl} alt={dealership.name} className="dealer-header-logo" />
                )}
                <span>{dealership.name}</span>
              </div>
            )}
            {user ? (
              <div className="user-menu" ref={dropdownRef}>
                <button className="user-name-button" onClick={() => setMenuOpen(!menuOpen)}>
                  {user.name || 'User'}
                </button>
              </div>
            ) : (
              <button className="login-button" onClick={() => setIsLoginModalOpen(true)}>
                Login / Sign Up
              </button>
            )}
          </div>

          <button
            className="hamburger-menu"
            onClick={() => setMenuOpen(true)}
            ref={hamburgerRef}
            aria-label="Open menu"
            aria-expanded={menuOpen}
          >
            &#9776;
          </button>
        </div>
      </div>

      <div className="header-bottom">
        <nav className="nav-links">
          <Link to="/build-your-ad">Build Your Ad</Link>
          <Link to="/promotions">Promotional Items</Link>
          <Link to="/orders">Orders</Link>
          <Link to="/account">Account</Link>
          <Link to="/cart" className="cart-link">
            Cart
            {cartItemCount > 0 && <span className="cart-badge">{cartItemCount}</span>}
          </Link>
        </nav>
      </div>


      {/* Backdrop and Sidebar — always mounted so open/close animates */}
      <div
        className={`slider-nav-backdrop${menuOpen ? ' open' : ''}`}
        onClick={() => setMenuOpen(false)}
      />
      <div
        className={`slider-nav${menuOpen ? ' open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
      >
      <div className="slider-nav-content">
        <button
          className="slider-close"
          aria-label="Close menu"
          onClick={() => setMenuOpen(false)}
          ref={closeButtonRef}
        >
          &times;
        </button>
        <div className="slider-dealer-stack">
          {dealership?.name && (
            <div className="dealer-header-name slider">
              {dealership.logoUrl && (
                <img src={dealership.logoUrl} alt={dealership.name} className="dealer-header-logo" />
              )}
              <span>{dealership.name}</span>
            </div>
          )}
          {user && <span className="slider-user">{user.name || 'User'}</span>}
        </div>
        <nav className="slider-nav-links">
          <Link to="/build-your-ad" onClick={() => setMenuOpen(false)}>Build Your Ad</Link>
          <Link to="/promotions" onClick={() => setMenuOpen(false)}>Promotional Items</Link>
          <Link to="/orders" onClick={() => setMenuOpen(false)}>Orders</Link>
          <Link to="/account" onClick={() => setMenuOpen(false)}>Account</Link>
          <Link to="/cart" onClick={() => setMenuOpen(false)}>
            Cart {cartItemCount > 0 && <span className="mobile-cart-badge">{cartItemCount}</span>}
          </Link>
          {!user ? (
            <div className="mobile-menu-item slider-btn-link" onClick={() => { setIsLoginModalOpen(true); setMenuOpen(false); }}>
              Login / Sign Up
            </div>
          ) : (
            <div className="mobile-menu-item slider-btn-link" onClick={() => { setIsLogoutModalOpen(true); setMenuOpen(false); }}>
              Logout
            </div>
          )}
        </nav>
      </div>
      </div>

      {isLoginModalOpen && <LoginModal onClose={() => setIsLoginModalOpen(false)} />}
      {isLogoutModalOpen && <LogoutConfirmationModal onClose={() => setIsLogoutModalOpen(false)} onConfirm={handleLogout} />}
    </header>
  );
};

export default Header;
